import { doc, type Firestore, type Transaction } from 'firebase/firestore';
import type { Expense, Transport, TripConfig } from '../types';
import { stripUndef, writeTransportChange } from './dayWrites';
import { prepareBookingExpense, type BookingCost } from './bookingExpenses';
import { expenseBaseCurrency } from './expenseUtils';
import { secondaryChargeRefs } from './expenseLinks';
import { assertLinkedCostUnchanged } from './bookingCostReview';
import { claimFullCost } from './fullCostProtocol';

type TransportDraft = Transport & BookingCost;
const label = (t: Transport) => t.isRental ? `Car hire${t.carrier ? ` · ${t.carrier}` : ''}` : t.flightNum
  ? `${t.carrier} ${t.flightNum} — ${t.fromCode || t.from} → ${t.toCode || t.to}`
  : `${t.mode} — ${t.from} → ${t.to}`;

/** Prepare outside the transaction: no network calls inside a retryable write. */
export async function prepareTransportExpense(trip: TripConfig, date: string, next: TransportDraft, userId: string,
  rateFor: (from: string, to: string, date?: string) => Promise<number>): Promise<Expense | undefined> {
  return prepareBookingExpense(trip, next, userId, {
    desc: label(next), date, cat: next.icon || '✈️', catName: next.mode, sourceType: 'transport',
  }, rateFor);
}

/** Reuse the identity/conflict-safe booking writer; expense changes commit with it. */
export async function writeTransportBooking(transaction: Transaction, db: Firestore, input: {
  tripId: string; date: string; previous: Transport | null; next: TransportDraft | null; userId: string; expense?: Expense;
}): Promise<Transport | null> {
  const { tripId, date, previous, userId, expense } = input;
  const tripRef = doc(db, 'trips', tripId);
  const trip = await transaction.get(tripRef);
  if (!trip.exists()) throw new Error('This trip is no longer available.');
  const expenseId = previous?.expenseLogId || expense?.id || input.next?.expenseLogId;
  const expenseRef = expenseId ? doc(tripRef, 'expenses', expenseId) : null;
  const snapshot = expenseRef ? await transaction.get(expenseRef) : null;
  const existing = snapshot?.exists() ? snapshot.data() as Expense : null;
  assertLinkedCostUnchanged(existing, previous, input.next);
  // Older rows can have an explicit expense link but no transport ID. The day
  // writer verifies that exact legacy row before upgrading its identity.
  const legacyLink = previous && !previous.id && previous.expenseLogId === expenseId;
  if (existing && ((existing.sourceType && existing.sourceType !== 'transport')
    || (!legacyLink && existing.sourceId && existing.sourceId !== previous?.id && existing.sourceId !== input.next?.id)
    || (!previous?.expenseLogId && (existing.sourceType !== 'transport' || existing.sourceId !== input.next?.id)))) {
    throw new Error('This expense is linked to a different booking. Reopen the transport to review it.');
  }
  if (existing?.itineraryDeleted && !previous) {
    throw new Error('This booking was removed. Reopen the itinerary before adding it again.');
  }
  let next = input.next && { ...input.next };
  if (next) {
    if (existing || expense) next.expenseLogId = expenseId;
    else {
      if (previous?.expenseLogId && next._logToExpenses) throw new Error('The linked expense was removed. Reopen this transport before logging a new cost.');
      delete next.expenseLogId;
    }
  }
  if (expense && !existing) {
    const day = await transaction.get(doc(tripRef, 'itinerary', date));
    const stored = (day.data()?.transports ?? []).find((t: Transport) => t.id === next?.id) as Transport | undefined;
    if (expense.amountBasis === 'full-cost' && stored
      && (await secondaryChargeRefs(transaction, db, tripId, stored, 'transport')).length) {
      throw new Error('This booking already has linked expenses. Review those charges before logging its full cost.');
    }
    if ((day.data()?.transports ?? []).some((t: Transport) => t.id === next?.id && t.expenseLogId)) {
      throw new Error('The linked expense was removed. Reopen this transport before logging a new cost.');
    }
    const latest = trip.data() as TripConfig;
    const active = (latest.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
    if (previous?.expenseLogId || !next || expense.sourceId !== next.id || expense.sourceType !== 'transport'
      || !active.includes(expense.paid_by) || !expense.split.length || expense.split.some(id => !active.includes(id))
      || expense.targetCurrency !== expenseBaseCurrency(latest)) {
      throw new Error('The trip or expense link changed. Reopen this transport before saving.');
    }
  }
  const secondaryRefs = !input.next && previous
    ? await secondaryChargeRefs(transaction, db, tripId, previous, 'transport') : [];
  // writeTransportChange performs the remaining reads before staging its write.
  const saved = await writeTransportChange(transaction, db, { tripId, date, previous, next });
  if (saved?.expenseLogId !== next?.expenseLogId) throw new Error('The linked expense changed on another device. Reopen this transport.');
  if (expenseRef && existing) {
    if (!next) transaction.update(expenseRef, { itineraryDeleted: true, itineraryDeletedBy: userId, itineraryDeletedAt: new Date().toISOString() });
    else if (previous) transaction.update(expenseRef, {
      desc: label(saved!), cat: saved!.icon || '✈️', catName: saved!.mode,
      sourceId: saved!.id, sourceType: 'transport',
      importedItemDate: /^\d{4}-\d{2}-\d{2}/.test(saved!.dep || '') ? saved!.dep.slice(0, 10) : date,
      ...(existing.itineraryDeleted ? { itineraryDeleted: false } : {}),
    });
  } else if (expenseRef && expense && saved) {
    transaction.set(expenseRef, claimFullCost(transaction, db, tripId, stripUndef({ ...expense,
      desc: label(saved), cat: saved.icon || '✈️', catName: saved.mode,
      sourceId: saved.id, sourceType: 'transport',
      importedItemDate: /^\d{4}-\d{2}-\d{2}/.test(saved.dep || '') ? saved.dep.slice(0, 10) : date,
    })));
  }
  for (const ref of secondaryRefs) transaction.update(ref, {
    itineraryDeleted: true, itineraryDeletedBy: userId, itineraryDeletedAt: new Date().toISOString(),
  });
  return saved;
}
